import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { Portal } from './Portal';
import { cn } from '@/shared/utils/cn';

export interface DropdownItem {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  align?: 'left' | 'right';
}

/**
 * Reusable dropdown component with Portal-based rendering and keyboard navigation
 * Supports ArrowDown, ArrowUp, Enter, Escape
 * Automatically positions to avoid viewport edges with proper z-index layering
 */
export function Dropdown({ trigger, items, align = 'left' }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: align === 'right' ? rect.right + window.scrollX : rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen, align]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(0);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => (prev + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev - 1 + items.length) % items.length);
        break;
      case 'Enter':
        e.preventDefault();
        if (!items[focusedIndex].disabled) {
          items[focusedIndex].onClick();
          setIsOpen(false);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setFocusedIndex(0);
        break;
    }
  };

  const handleItemClick = (item: DropdownItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
      setFocusedIndex(0);
    }
  };

  return (
    <div ref={dropdownRef} className="relative inline-block" onKeyDown={handleKeyDown}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={isOpen}
        data-interaction-id='div-set-is-open'>
        {trigger}
      </div>
      {isOpen && (
        <Portal>
          <div
            className="fixed md:absolute"
            style={{
              top: window.innerWidth < 768 ? 'auto' : `${dropdownPosition.top + 8}px`,
              left: window.innerWidth < 768 ? 0 : align === 'right' ? 'auto' : `${dropdownPosition.left}px`,
              right: window.innerWidth < 768 ? 0 : align === 'right' ? `${window.innerWidth - dropdownPosition.left}px` : 'auto',
              width: window.innerWidth < 768 ? '100%' : 'auto',
              minWidth: window.innerWidth >= 768 ? '200px' : undefined,
              bottom: window.innerWidth < 768 ? 0 : 'auto',
              zIndex: 99999, // Above modals (z-50) to prevent clipping
            }}
          >
            <div
              ref={menuRef}
              className={cn(
                'bg-dark-bg border-t md:border border-dark-border/30 rounded-t-2xl md:rounded-xl shadow-2xl shadow-black/20',
                'theme-dynamics:shadow-elevation-3',
                'theme-dynamics:md:rounded-medium'
              )}
              role="menu"
            >
              {items.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleItemClick(item)}
                  disabled={item.disabled}
                  className={`
                    w-full px-4 py-3 min-h-[44px] md:min-h-0 md:py-2 text-left text-sm transition-all duration-150
                    flex items-center gap-2
                    ${
                      item.disabled
                        ? 'text-dark-muted cursor-not-allowed'
                        : 'text-white hover:bg-dark-surface active:bg-dark-input'
                    }
                    ${focusedIndex === index ? 'bg-dark-surface' : ''}
                    ${index === 0 ? 'rounded-t-2xl md:rounded-t-lg' : ''}
                    ${index === items.length - 1 ? 'rounded-b-2xl md:rounded-b-lg' : ''}
                  `}
                  role="menuitem"
                  tabIndex={-1}
                  data-testid='unknown-action-btn'
                  data-ux='action'
                  data-interaction-id='unknown-action-btn'>
                  {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
